/**
 * Marketing attribution for outbound Sensei 3D Designer links.
 *
 * Ported from the ProBuilt Steel site (src/utils/tracking.js) so both sites
 * capture, persist and forward attribution the same proven way:
 *
 *   - capture every non-empty attribution parameter from the landing URL into
 *     sessionStorage, trimmed, without overwriting a stored value with an
 *     empty/missing one;
 *   - read them back (current-URL values take priority because capture runs
 *     first and overwrites storage with any fresh non-empty value);
 *   - build the Designer URL with URL/URLSearchParams so existing query
 *     parameters are preserved and no empty values are ever appended.
 *
 * Every entry point is SSR-guarded: on the server these are no-ops that return
 * safe defaults, so Gatsby build and hydration are unaffected.
 *
 * These keys match the ones the lead-attribution flow (src/utils/submitLead.js
 * and gatsby-browser.js) already reads and writes, so there is a single set of
 * sessionStorage keys across the site.
 */

export const TRACKING_PARAMETERS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
]

/**
 * Stores attribution parameters from the current URL in sessionStorage.
 *
 * Only writes a key when the URL carries a non-empty value, so navigating to a
 * page without a given parameter never clears what an earlier page stored.
 * Values are kept exactly as received (only surrounding whitespace trimmed).
 */
export const captureTrackingParameters = () => {
  if (typeof window === "undefined") {
    return
  }

  try {
    const searchParams = new URLSearchParams(window.location.search)

    const hasTrackingParameter = TRACKING_PARAMETERS.some(parameter => {
      const value = searchParams.get(parameter)
      return value !== null && value.trim() !== ""
    })

    if (!hasTrackingParameter) {
      return
    }

    TRACKING_PARAMETERS.forEach(parameter => {
      const value = searchParams.get(parameter)
      if (value !== null && value.trim() !== "") {
        window.sessionStorage.setItem(parameter, value.trim())
      }
    })

    // Store the full landing URL (minus fbclid) for reference/reporting.
    const urlWithoutFbclid = new URL(window.location.href)
    urlWithoutFbclid.searchParams.delete("fbclid")
    window.sessionStorage.setItem("utm_url", urlWithoutFbclid.toString())
  } catch (error) {
    // Attribution is best-effort; never let it break the page.
  }
}

/**
 * Reads the stored attribution values. utm_source falls back to "Website"
 * (matching ProBuilt) when nothing was ever captured.
 */
export const getTrackingParameters = () => {
  const defaultValues = {
    utmSource: "Website",
    utmMedium: "",
    utmCampaign: "",
    utmTerm: "",
    utmContent: "",
    gclid: "",
    gbraid: "",
    wbraid: "",
    fbclid: "",
    fullUrl: "",
  }

  if (typeof window === "undefined") {
    return defaultValues
  }

  try {
    const read = key => window.sessionStorage.getItem(key) || ""
    return {
      utmSource: window.sessionStorage.getItem("utm_source") || "Website",
      utmMedium: read("utm_medium"),
      utmCampaign: read("utm_campaign"),
      utmTerm: read("utm_term"),
      utmContent: read("utm_content"),
      gclid: read("gclid"),
      gbraid: read("gbraid"),
      wbraid: read("wbraid"),
      fbclid: read("fbclid"),
      fullUrl: window.sessionStorage.getItem("utm_url") || window.location.href,
    }
  } catch (error) {
    return defaultValues
  }
}

/**
 * Builds a Designer URL with the stored attribution appended.
 *
 * Captures the current URL's parameters first so a fresh landing value takes
 * priority over an older stored one, then appends each non-empty value with
 * URLSearchParams.set — which preserves any query parameters already on
 * baseUrl and can never produce "?&", "&&", "undefined" or "null".
 *
 * Returns baseUrl unchanged on the server, on any error, or when there is no
 * attribution to append.
 */
export const buildTrackedUrl = baseUrl => {
  if (typeof window === "undefined") {
    return baseUrl
  }

  try {
    captureTrackingParameters()

    const url = new URL(baseUrl)

    // Read raw stored values rather than getTrackingParameters() here: the
    // latter defaults utm_source to "Website", which would append
    // utm_source=Website even when the visitor arrived with no attribution.
    // The spec requires the Designer URL to be left unchanged in that case, so
    // an unset parameter must stay empty and be omitted below.
    const read = key => window.sessionStorage.getItem(key) || ""
    const parameters = {
      utm_source: read("utm_source"),
      utm_medium: read("utm_medium"),
      utm_campaign: read("utm_campaign"),
      utm_term: read("utm_term"),
      utm_content: read("utm_content"),
      gclid: read("gclid"),
      gbraid: read("gbraid"),
      wbraid: read("wbraid"),
      fbclid: read("fbclid"),
    }

    Object.entries(parameters).forEach(([key, value]) => {
      if (
        value !== null &&
        value !== undefined &&
        String(value).trim() !== ""
      ) {
        url.searchParams.set(key, String(value).trim())
      }
    })

    return url.toString()
  } catch (error) {
    return baseUrl
  }
}
